import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheckDouble,
  faGear,
  faPlus,
  faSliders,
} from '@fortawesome/free-solid-svg-icons';

import {
  NotificationServiceContextProvider,
  useNotificationServiceContext,
  STATUS_OPTIONS,
  SubscriptionWizardModal,
  Subscription,
  NotificationInboxEntry,
} from '@/core/notifications/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { useHasPermission, Permissions } from '@/core/permissions/export';
import { useIsMobile } from '@/shared/hooks/export';
import { Loading, CheckboxRow, MenuList, MenuListItem } from '@/shared/components/export';
import { PageControl } from '@/shared/pagination/export';
import PipelineSubscriptionsModal from '@/modules/pipelines/components/deep-dive/notifications/pipeline-subscriptions-modal';
import {
  PAGE_SIZES,
  PIPELINE_PUBLISHER_NAMES,
  usePipelineNotifications,
} from '@/modules/pipelines/hooks/use-pipeline-notifications';
import styles from '@/modules/pipelines/components/deep-dive/pipeline-map-tabs.module.scss';

const ALL_SUBSCRIPTIONS = '';

function PipelineNotificationsView() {
  const inbox = usePipelineNotifications();
  const { subscriptions, topics, applications, channels, topicByKey, refreshSubscriptions } =
    useNotificationServiceContext();
  const openWidget = useOpenWidget();
  const isMobile = useIsMobile(576);
  const canManageSubscriptions = useHasPermission(Permissions.NOTIFICATIONS);
  const [subscriptionForm, setSubscriptionForm] = useState<{
    subscription: Subscription | null;
  } | null>(null);
  const [managingSubscriptions, setManagingSubscriptions] = useState(false);

  const pipelineSubscriptions = useMemo(() => {
    if (!inbox.publisherId) return [];
    return subscriptions.filter(sub => topicByKey(sub.topic)?.applicationId === inbox.publisherId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, inbox.publisherId]);

  const pipelineTopics = useMemo(
    () => (inbox.publisherId ? topics.filter(t => t.applicationId === inbox.publisherId) : []),
    [topics, inbox.publisherId],
  );

  const selectedCount = inbox.selectedIds.size;

  const subscriptionMenuItems: MenuListItem[] = [
    {
      key: 'new',
      label: 'New alarm…',
      icon: faPlus,
      disabled: pipelineTopics.length === 0,
      onSelect: () => setSubscriptionForm({ subscription: null }),
    },
    {
      key: 'manage',
      label: 'Manage pipeline alarms ',
      icon: faSliders,
      disabled: pipelineSubscriptions.length === 0,
      badge: pipelineSubscriptions.length || undefined,
      onSelect: () => setManagingSubscriptions(true),
    },
    { key: 'settings-divider', label: '', divider: true },
    {
      key: 'settings',
      label: 'All notification settings…',
      icon: faGear,
      onSelect: () => openWidget('settings', 'notifications'),
    },
  ];

  return (
    <>
      <div className={'d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2'}>
        <div className={'d-flex align-items-center gap-2'}>
          {inbox.publisherName && (
            <span className={styles.filterLabel} style={{ margin: 0 }}>
              {inbox.publisherName}
            </span>
          )}
          {inbox.unreadCount > 0 && (
            <span className={'badge bg-danger'}>
              {inbox.unreadCount >= 99 ? '99+' : inbox.unreadCount} unread
            </span>
          )}
        </div>

        <div className={'d-flex align-items-center gap-2'}>
          <button
            type={'button'}
            className={'btn btn-sm btn-outline-secondary d-flex align-items-center gap-1'}
            disabled={selectedCount === 0}
            onClick={inbox.markSelectedRead}
          >
            <FontAwesomeIcon icon={faCheckDouble} size={'xs'} />
            {selectedCount > 0 ? `Mark ${selectedCount} as read` : 'Mark selected as read'}
          </button>
          {canManageSubscriptions && (
            <MenuList
              trigger={
                <>
                  <FontAwesomeIcon icon={faBell} size={'xs'} /> Alarms
                </>
              }
              align={'right'}
              minWidth={240}
              elevated
              toggleClassName={'btn btn-sm btn-outline-secondary d-flex align-items-center gap-1'}
              toggleTitle={'Create or edit pipeline notification alarms'}
              items={subscriptionMenuItems}
            />
          )}
        </div>
      </div>

      <div className={`${styles.filterRow} mb-2`}>
        <span className={styles.filterLabel}>Status</span>
        {STATUS_OPTIONS.map(option => (
          <button
            key={option.value}
            type={'button'}
            className={`${styles.filterChip} ${
              option.value === inbox.statusFilter ? styles.filterChipActive : ''
            }`}
            aria-pressed={option.value === inbox.statusFilter}
            onClick={() => inbox.setStatusFilter(option.value)}
          >
            {option.label}
          </button>
        ))}

        {pipelineSubscriptions.length > 0 && (
          <>
            <span className={styles.filterLabel}>Alarm</span>
            <select
              className={styles.filterSelect}
              value={inbox.selectedSubscription ?? ALL_SUBSCRIPTIONS}
              onChange={event => inbox.setSelectedSubscription(event.target.value || undefined)}
              aria-label={'Show only notifications delivered by one alarm'}
            >
              <option value={ALL_SUBSCRIPTIONS}>All pipeline alarms</option>
              {pipelineSubscriptions.map(sub => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {inbox.publisherMissing ? (
        <div className={styles.error}>
          No pipeline notifications publisher is registered in this environment — looked for{' '}
          {PIPELINE_PUBLISHER_NAMES.map(name => `“${name}”`).join(' or ')}.
        </div>
      ) : (
        <>
          <div className={'d-flex align-items-center gap-3 px-3 py-2 border-top border-bottom'}>
            <CheckboxRow
              checked={inbox.allSelected}
              indeterminate={selectedCount > 0 && !inbox.allSelected}
              onChange={inbox.toggleSelectAll}
              disabled={inbox.notifications.length === 0}
              label={'Select all'}
              title={'Select every notification on this page'}
              className={'p-0'}
            />
            <span className={'mutedText'} style={{ fontSize: 12 }}>
              {selectedCount > 0
                ? `${selectedCount} selected`
                : `${inbox.notifications.length} shown`}
            </span>
          </div>

          <div className={`${styles.content} overflow-y-auto`}>
            {inbox.loading && <Loading />}

            {!inbox.loading && inbox.notifications.length === 0 && (
              <div className={'p-4 text-center mutedText'} style={{ fontSize: 13 }}>
                No pipeline notifications
              </div>
            )}

            {!inbox.loading &&
              inbox.notifications.map(n => {
                const selected = inbox.selectedIds.has(n.notification.id);
                return (
                  <div
                    key={n.id}
                    className={'d-flex align-items-stretch'}
                    style={
                      selected ? { background: 'rgba(var(--bs-primary-400-rgb), 0.12)' } : undefined
                    }
                  >
                    <div className={'d-flex align-items-center ps-3 border-bottom'}>
                      <CheckboxRow
                        checked={selected}
                        onChange={() => inbox.toggleSelected(n.notification.id)}
                        title={'Select this notification'}
                        className={'p-0'}
                      />
                    </div>
                    <div className={'flex-grow-1'} style={{ minWidth: 0 }}>
                      <NotificationInboxEntry
                        notification={n}
                        isMobile={isMobile}
                        onMarkRead={() => inbox.markRead(n.notification.id)}
                        onMarkUnread={() => inbox.markUnread(n.notification.id)}
                      />
                    </div>
                  </div>
                );
              })}
          </div>

          <div className={'border-top pt-1 pb-3'}>
            <PageControl
              offset={inbox.page}
              onChange={inbox.setPage}
              hasMore={inbox.hasMore}
              menu={
                <label className={'d-flex align-items-center gap-1 mb-0'}>
                  <span className={styles.filterLabel}>Per page</span>
                  <select
                    className={styles.filterSelect}
                    value={inbox.pageSize}
                    onChange={event => inbox.setPageSize(Number(event.target.value))}
                    aria-label={'Notifications per page'}
                  >
                    {PAGE_SIZES.map(size => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              }
            />
          </div>
        </>
      )}

      {managingSubscriptions && (
        <PipelineSubscriptionsModal
          subscriptions={pipelineSubscriptions}
          topics={pipelineTopics}
          applications={applications}
          channels={channels}
          onClose={() => setManagingSubscriptions(false)}
          onChanged={refreshSubscriptions}
        />
      )}

      {subscriptionForm && (
        <SubscriptionWizardModal
          existingSubscription={subscriptionForm.subscription}
          topics={pipelineTopics}
          applications={applications}
          channels={channels}
          onCancel={() => setSubscriptionForm(null)}
          onDone={() => {
            refreshSubscriptions();
            setSubscriptionForm(null);
          }}
        />
      )}
    </>
  );
}

export default function PipelineNotificationsPanel() {
  return (
    <div className={styles.page}>
      <NotificationServiceContextProvider>
        <PipelineNotificationsView />
      </NotificationServiceContextProvider>
    </div>
  );
}
